// Service layer for the "Understand Something" guided concept-mastery flow
// (Phase 1 of the Branon redesign). Calls the `guided-session` edge function,
// one typed function per action — matches the fetch/Authorization pattern
// used by generateAppliedQuestions() in conceptPlus1Service.ts.

import { supabase, supabaseUrl, supabaseAnonKey } from "./supabaseClient";

export type Depth = "quick_recap" | "explain_properly" | "lets_just_solve";
// Phase 11: independent from Depth. Depth answers "what do you want to do
// right now"; Level answers "at what academic rigor" -- student-chosen,
// defaulting from their enrolled grade but never gated by it (see
// defaultLevelForGrade below and ai_coach_plan.md Phase 11).
export type Level = "foundation" | "standard" | "advanced" | "competitive";
export type PracticeMode = "mcq" | "short_answer" | "long_answer";
export type EvalState = "understood" | "partially_understood" | "misconception" | "needs_reinforcement";

export interface GuidedConceptContext {
  concept_name: string;
  subject: string;
  grade_level: number;
  explanation_basic?: string;
  explanation_standard?: string;
  explanation_advanced?: string;
  real_life_example?: string;
  common_mistakes?: string;
  // Phase 3: present for an upload/paste session instead of a real concept.
  raw_input_text?: string;
  // Phase 11: which competitive exam(s) this subject maps to (e.g. "JEE",
  // "NEET", "JEE and NEET"), only meaningful when level === "competitive".
  competitive_context?: string;
}

// Grade sets the *default* level only -- it never gates which levels are
// reachable (a Grade 11 student can drop to foundation for a fundamental
// gap, or reach for advanced/competitive, with no friction either way).
export function defaultLevelForGrade(gradeLevel: number): Level {
  if (gradeLevel <= 8) return "foundation";
  if (gradeLevel <= 10) return "standard";
  return "advanced";
}

// Static, small, rarely-changing subject->competitive-exam mapping (not a DB
// column -- this doesn't need per-concept authoring). Subjects absent from
// this map have no competitive-exam context, so the Competitive tab is
// hidden entirely for them (and for raw/custom topics, where the subject is
// never reliably known).
const COMPETITIVE_SUBJECTS: Record<string, string> = {
  Math: "JEE",
  Mathematics: "JEE",
  Physics: "JEE and NEET",
  Chemistry: "JEE and NEET",
  Biology: "NEET",
};

export function competitiveContextForSubject(subject: string | undefined): string | undefined {
  if (!subject) return undefined;
  return COMPETITIVE_SUBJECTS[subject];
}

export function subjectSupportsCompetitive(subject: string | undefined): boolean {
  return competitiveContextForSubject(subject) !== undefined;
}

interface StartSessionResult {
  session_id: number;
  phase: string;
  depth: Depth;
  level: Level;
  resumed: boolean;
  error?: string;
}

interface ExplainResult {
  explanation: string;
  real_life_example?: string;
  quick_check?: string;
  sub_topics?: string[];
  phase: string;
  error?: string;
}

interface GuidedPracticeTurnResult {
  reply: string;
  done: boolean;
  phase: string;
  error?: string;
}

interface ChoosePracticeModeResult {
  practice_mode?: PracticeMode;
  question?: string;
  option_a?: string;
  option_b?: string;
  option_c?: string;
  option_d?: string;
  phase?: string;
  max_reached?: boolean;
  error?: string;
}

// Coarse, UI-safe status — the full 4-state EvalState (understood/
// partially_understood/needs_reinforcement/misconception) is internal to the
// guided-session edge function (mastery-engine scoring + question_history)
// and is never sent to the client; this is what's translated for display.
export type UiUnderstandingStatus = "solid" | "mostly_there" | "needs_review";

interface EvaluateAnswerResult {
  was_strong: boolean;
  ui_status: UiUnderstandingStatus;
  feedback: string;
  should_conclude: boolean;
  error?: string;
}

export interface GuidedQuestionHistoryEntry {
  question: string;
  question_type: PracticeMode;
  options: { option_a: string; option_b: string; option_c: string; option_d: string } | null;
  student_answer: number | string | null;
  correct_answer: string | null;
  solution_steps: string[];
  topic_note: string;
  difficulty: Level;
  created_at: string;
  ui_status: UiUnderstandingStatus;
  ui_message: string;
}

interface ConcludeResult {
  message: string;
  phase: string;
  question_history?: GuidedQuestionHistoryEntry[];
  error?: string;
}

async function callGuidedSession<T>(payload: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${supabaseUrl}/functions/v1/guided-session`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${supabaseAnonKey}` },
    body: JSON.stringify(payload),
  });
  return response.json();
}

export function startGuidedSession(params: {
  studentId: string;
  conceptId?: number;
  conceptTable?: "concepts" | "concepts_plus1";
  depth: Depth;
  level: Level;
  sourceType?: "concept" | "image" | "paste";
  rawInputText?: string;
}): Promise<StartSessionResult> {
  return callGuidedSession({
    action: "start",
    student_id: params.studentId,
    concept_id: params.conceptId,
    concept_table: params.conceptTable ?? "concepts",
    depth: params.depth,
    level: params.level,
    source_type: params.sourceType ?? "concept",
    raw_input_text: params.rawInputText,
  });
}

export function explainAtDepth(
  sessionId: number,
  concept: GuidedConceptContext,
  level: Level,
  depth: Depth,
  focusNote?: string
): Promise<ExplainResult> {
  return callGuidedSession({ action: "explain", session_id: sessionId, concept, level, depth, focus_note: focusNote });
}

export function sendGuidedPracticeTurn(
  sessionId: number,
  concept: GuidedConceptContext,
  level: Level,
  depth: Depth,
  studentMessage?: string
): Promise<GuidedPracticeTurnResult> {
  return callGuidedSession({ action: "guided_practice_turn", session_id: sessionId, concept, level, depth, student_message: studentMessage });
}

export function choosePracticeMode(
  sessionId: number,
  concept: GuidedConceptContext,
  practiceMode: PracticeMode,
  level: Level,
  depth: Depth
): Promise<ChoosePracticeModeResult> {
  return callGuidedSession({ action: "choose_practice_mode", session_id: sessionId, concept, practice_mode: practiceMode, level, depth });
}

export function evaluatePracticeAnswer(params: {
  sessionId: number;
  concept?: GuidedConceptContext;
  optionChosen?: number;
  answerText?: string;
  level?: Level;
  depth?: Depth;
}): Promise<EvaluateAnswerResult> {
  return callGuidedSession({
    action: "evaluate_practice_answer",
    session_id: params.sessionId,
    concept: params.concept,
    option_chosen: params.optionChosen,
    answer_text: params.answerText,
    level: params.level,
    depth: params.depth,
  });
}

export function concludeGuidedSession(sessionId: number, concept: GuidedConceptContext, level: Level, depth: Depth): Promise<ConcludeResult> {
  return callGuidedSession({ action: "conclude", session_id: sessionId, concept, level, depth });
}

// ── History (migration 10072) ─────────────────────────────────
// Reads guided_sessions.question_history back after a session has already
// concluded — the live flow above only ever sees it once, at conclude time.

export interface GuidedSessionHistoryItem {
  session_id: number;
  concept_name: string;
  level: Level;
  depth: Depth;
  updated_at: string;
  question_count: number;
  solid_count: number;
  needs_review_count: number;
}

export interface GuidedSessionDetail {
  success: boolean;
  error?: string;
  concept_name: string;
  level: Level;
  depth: Depth;
  updated_at: string;
  conclude_message: string;
  question_history: GuidedQuestionHistoryEntry[];
}

export async function getGuidedSessionsHistory(studentId: string, limit = 30): Promise<GuidedSessionHistoryItem[]> {
  const { data, error } = await supabase.rpc("get_guided_sessions_history", {
    p_student_id: studentId,
    p_limit: limit,
  });
  if (error) {
    console.error("Error loading guided session history:", error);
    return [];
  }
  return (data as GuidedSessionHistoryItem[]) ?? [];
}

export async function getGuidedSessionDetail(studentId: string, sessionId: number): Promise<GuidedSessionDetail | null> {
  const { data, error } = await supabase.rpc("get_guided_session_detail", {
    p_student_id: studentId,
    p_session_id: sessionId,
  });
  if (error) {
    console.error("Error loading guided session detail:", error);
    return null;
  }
  return data as GuidedSessionDetail;
}

interface DecodeUploadResult {
  text?: string;
  error?: string;
}

// Claude's vision API processes images best up to ~1568px on the long edge --
// larger photos get downscaled internally in a way this app can't verify, which
// risks losing fine detail (e.g. small/dense script in a table) before the model
// ever sees it. Resizing client-side, deterministically, avoids relying on that
// opaque internal downscaling.
const MAX_UPLOAD_DIMENSION = 1568;

export async function resizeImageFileForUpload(file: File): Promise<{ base64: string; mediaType: string }> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load that image."));
    img.src = dataUrl;
  });

  const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(image.width, image.height));
  const targetWidth = Math.round(image.width * scale);
  const targetHeight = Math.round(image.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process that image.");
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  const resizedDataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const [prefix, base64] = resizedDataUrl.split(",");
  const mediaType = prefix.match(/data:(.*);base64/)?.[1] ?? "image/jpeg";
  return { base64, mediaType };
}

// Phase 3 (universal upload/decode-confirm): one-shot Claude vision call.
// Separate edge function from guided-session -- a single-purpose OCR-style
// transcription, not part of the multi-turn engine. The image itself is
// never persisted; only the returned text is used.
export async function decodeUploadedImage(params: {
  imageBase64: string;
  mediaType: string;
  gradeLevel?: number;
  subject?: string;
}): Promise<DecodeUploadResult> {
  const response = await fetch(`${supabaseUrl}/functions/v1/decode-upload`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${supabaseAnonKey}` },
    body: JSON.stringify({
      image_base64: params.imageBase64,
      media_type: params.mediaType,
      grade_level: params.gradeLevel,
      subject: params.subject,
    }),
  });
  return response.json();
}
